import { useEffect, useState, useCallback } from "react";
import { API_BASE } from "../lib/api";

const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY;

// ══════════════════════════════════════════════════════════════════════
// Types
// ══════════════════════════════════════════════════════════════════════

interface ChatAnalyticsResponse {
  overview: {
    total_responses: number;
    total_sessions: number;
    research_uses: number;
    avg_tokens: number;
    total_tokens: number;
    tool_activations: number;
    avg_tool_rounds: number;
    health_score: number;
  } | null;
  broken_paths: Array<{
    id: string;
    severity: "high" | "medium";
    count: number;
    title: string;
    description: string;
    fix: string;
  }> | null;
  daily_volume: Array<{
    day: string;
    questions: number;
    responses: number;
  }> | null;
  recent_sessions: Array<{
    id: string;
    title: string | null;
    created_at: string | null;
    speaker_id: string | null;
    user_turns: number;
    max_tokens: number | null;
    research_mode: number | null;
    tools_used: string | null;
    tool_rounds: number | null;
    cited_responses: number;
  }> | null;
  top_cited_episodes: Array<{
    episode_title: string;
    citation_count: number;
  }> | null;
  code_path_stats: {
    standard_with_citations: number;
    standard_no_citations: number;
    research_with_tools: number;
    research_no_tools: number;
    speaker_mode_sessions: number;
    gemini_fallback: number;
    total_tool_rounds: number;
  } | null;
}

// ══════════════════════════════════════════════════════════════════════
// Helper Components
// ══════════════════════════════════════════════════════════════════════

function MetricCard({
  title,
  value,
  subtitle,
  status,
  large,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  status?: "green" | "amber" | "red";
  large?: boolean;
}) {
  const borderColor = status === "green" ? "border-green-500" : status === "amber" ? "border-amber-500" : status === "red" ? "border-red-500" : "border-gray-200";
  
  return (
    <div className={`rounded-lg border bg-white p-4 ${status ? `border-l-4 ${borderColor}` : "border-gray-200"}`}>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className={`mt-2 font-semibold text-gray-900 ${large ? "text-3xl" : "text-2xl"}`}>
        {value}
      </p>
      {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "-";
  }
}

// ══════════════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════════════

export default function AdminChatAnalyticsView() {
  const [data, setData] = useState<ChatAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/admin/chat/analytics?days=${days}`, {
        headers: { "X-Admin-Key": ADMIN_KEY },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load chat analytics");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
        {error}
        <button onClick={fetchData} className="ml-3 font-medium underline hover:text-red-800">
          Retry
        </button>
      </div>
    );
  }

  const { overview, broken_paths, daily_volume, recent_sessions, top_cited_episodes, code_path_stats } = data || {};

  // Health score status
  const healthStatus = overview?.health_score != null 
    ? overview.health_score >= 80 ? "green" : overview.health_score >= 60 ? "amber" : "red"
    : undefined;

  // Avg tokens status
  const avgTokensStatus = overview?.avg_tokens != null
    ? overview.avg_tokens > 50000 ? "red" : overview.avg_tokens > 20000 ? "amber" : undefined
    : undefined;

  // Max citation count (for detecting retrieval bias)
  const sortedEpisodes = top_cited_episodes?.slice().sort((a, b) => b.citation_count - a.citation_count) || [];
  const maxCitations = sortedEpisodes[0]?.citation_count || 0;
  const secondCitations = sortedEpisodes[1]?.citation_count || 0;
  const hasRetrievalBias = maxCitations > 3 * secondCitations && secondCitations > 0;

  // Daily volume chart scaling
  const maxQuestions = daily_volume?.length 
    ? Math.max(...daily_volume.map(d => d.questions), 1) 
    : 1;

  return (
    <div className="space-y-6">
      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section 1: Header */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            className="rounded-md border border-gray-300 p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
            title="Refresh"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
              <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H4.598a.75.75 0 0 0-.75.75v3.634a.75.75 0 0 0 1.5 0v-2.033l.312.311a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.06-7.19a.75.75 0 0 0-1.5 0v2.033l-.312-.312A7 7 0 0 0 2.848 9.093a.75.75 0 0 0 1.449.39A5.5 5.5 0 0 1 13.89 7.11l.311.312h-2.432a.75.75 0 0 0 0 1.5h3.634a.75.75 0 0 0 .75-.75V4.538Z" clipRule="evenodd" />
            </svg>
          </button>
          <h2 className="text-xl font-semibold text-gray-900">Chat Quality</h2>
        </div>
        <div className="flex gap-1">
          {[7, 14, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`rounded-md px-3 py-1 text-sm font-medium transition ${
                days === d
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section 2: Health Score + KPI Cards */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <MetricCard
            title="Health Score"
            value={`${overview.health_score ?? 0}%`}
            status={healthStatus}
            large
          />
          <MetricCard
            title="Total Responses"
            value={overview.total_responses?.toLocaleString() ?? 0}
          />
          <MetricCard
            title="Research Uses"
            value={overview.research_uses?.toLocaleString() ?? 0}
          />
          <MetricCard
            title="Avg Tokens"
            value={Math.round(overview.avg_tokens ?? 0).toLocaleString()}
            status={avgTokensStatus}
          />
          <MetricCard
            title="Tool Activations"
            value={overview.tool_activations?.toLocaleString() ?? 0}
          />
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section 3: Broken Paths */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {broken_paths && broken_paths.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-700">⚠️ Broken Paths</h3>
          {broken_paths.map((bp) => (
            <div
              key={bp.id}
              className={`rounded-lg border bg-white p-4 ${
                bp.severity === "high" ? "border-l-4 border-l-red-500" : "border-l-4 border-l-amber-500"
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900">{bp.title}</p>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      bp.severity === "high" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                    }`}>
                      {bp.count} responses
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">{bp.description}</p>
                  <div className="mt-2 rounded bg-gray-50 px-3 py-2 font-mono text-xs text-gray-600">
                    FIX → {bp.fix}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <p className="text-sm font-medium text-green-700">✅ No broken paths detected</p>
          <p className="text-xs text-green-600 mt-1">All chat code paths are functioning correctly</p>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section 4: Daily Volume Bar Chart */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {daily_volume && daily_volume.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">📈 Daily Volume</h3>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="flex items-end gap-1 h-32">
              {daily_volume.map((day) => {
                const height = (day.questions / maxQuestions) * 100;
                return (
                  <div key={day.day} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full rounded-t bg-blue-400 hover:bg-blue-500 transition-colors cursor-default"
                      style={{ height: `${Math.max(height, 2)}%` }}
                      title={`${day.questions} questions`}
                    />
                    <p className="text-xs text-gray-500 mt-1 truncate w-full text-center">
                      {formatShortDate(day.day)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section 5: Two-Column Row */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Sessions Table */}
        {recent_sessions && recent_sessions.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">💬 Recent Sessions</h3>
            <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Title</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">Turns</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">Mode</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-600">Cited</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-600">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {recent_sessions.slice(0, 10).map((session) => {
                    const isLowQuality = session.cited_responses === 0 && session.user_turns > 1;
                    const citedFraction = session.user_turns > 0 
                      ? `${session.cited_responses}/${session.user_turns}` 
                      : "-";
                    const title = session.title 
                      ? (session.title.length > 40 ? session.title.slice(0, 40) + "…" : session.title)
                      : "Untitled";
                    
                    return (
                      <tr 
                        key={session.id} 
                        className={isLowQuality ? "bg-amber-50" : ""}
                      >
                        <td className="px-3 py-2 text-gray-900 truncate max-w-[200px]" title={session.title || "Untitled"}>
                          {title}
                        </td>
                        <td className="px-3 py-2 text-center text-gray-600">
                          {session.user_turns}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {session.research_mode === 1 ? (
                            <span className="rounded-full bg-cyan-100 px-2 py-0.5 text-xs font-medium text-cyan-700">
                              research
                            </span>
                          ) : session.speaker_id ? (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                              speaker
                            </span>
                          ) : null}
                        </td>
                        <td className={`px-3 py-2 text-center font-medium ${
                          session.cited_responses === 0 ? "text-red-600" : "text-gray-600"
                        }`}>
                          {citedFraction}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-500">
                          {formatShortDate(session.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Top Cited Episodes */}
        {sortedEpisodes.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">📚 Top Cited Episodes</h3>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="space-y-2">
                {sortedEpisodes.slice(0, 10).map((ep, idx) => {
                  const barWidth = maxCitations > 0 ? (ep.citation_count / maxCitations) * 100 : 0;
                  const isBiased = idx === 0 && hasRetrievalBias;
                  const title = ep.episode_title.length > 35 
                    ? ep.episode_title.slice(0, 35) + "…" 
                    : ep.episode_title;
                  
                  return (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="min-w-0 flex-1 truncate text-sm text-gray-700" title={ep.episode_title}>
                        {title}
                      </div>
                      <div className="w-24 h-4 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                        <div
                          className={`h-full rounded ${isBiased ? "bg-amber-400" : "bg-blue-400"}`}
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                      <div className={`w-8 text-right text-xs font-medium ${isBiased ? "text-amber-600" : "text-gray-600"}`}>
                        {ep.citation_count}
                      </div>
                    </div>
                  );
                })}
              </div>
              {hasRetrievalBias && (
                <p className="mt-3 text-xs text-amber-600">
                  ⚠️ Top episode has 3× more citations than second — potential retrieval bias
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* Section 6: Code Path Stats */}
      {/* ════════════════════════════════════════════════════════════════ */}
      {code_path_stats && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">🔀 Code Path Stats</h3>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {code_path_stats.standard_with_citations?.toLocaleString() ?? 0}
                </p>
                <p className="text-xs text-gray-500">Standard + citations</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {code_path_stats.standard_no_citations?.toLocaleString() ?? 0}
                </p>
                <p className="text-xs text-gray-500">Standard no citations</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {code_path_stats.research_with_tools?.toLocaleString() ?? 0}
                </p>
                <p className="text-xs text-gray-500">Research + tools</p>
              </div>
              <div className="text-center">
                <p className={`text-2xl font-bold ${(code_path_stats.research_no_tools ?? 0) > 0 ? "text-red-600" : "text-gray-900"}`}>
                  {code_path_stats.research_no_tools?.toLocaleString() ?? 0}
                </p>
                <p className="text-xs text-gray-500">Research no tools</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {code_path_stats.speaker_mode_sessions?.toLocaleString() ?? 0}
                </p>
                <p className="text-xs text-gray-500">Speaker mode sessions</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">
                  {code_path_stats.gemini_fallback?.toLocaleString() ?? 0}
                </p>
                <p className="text-xs text-gray-500">Gemini fallback</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
